import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Chip,
  Button,
  TextField,
  Grid,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import FilterListIcon from '@mui/icons-material/FilterList';
import TerrainIcon from '@mui/icons-material/Terrain';
import CastleIcon from '@mui/icons-material/Castle';
import NatureIcon from '@mui/icons-material/Nature';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SecurityIcon from '@mui/icons-material/Security';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import ScienceIcon from '@mui/icons-material/Science';
import ForestIcon from '@mui/icons-material/Forest';
import ConstructionIcon from '@mui/icons-material/Construction';
// Fantasy-themed icons
import GrainIcon from '@mui/icons-material/Grain';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DiamondIcon from '@mui/icons-material/Diamond';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import HandymanIcon from '@mui/icons-material/Handyman';
// Additional fantasy icons for land types
import FortIcon from '@mui/icons-material/Fort';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
// Geography (terrain type) icons
import WaterIcon from '@mui/icons-material/Water';
import LandscapeIcon from '@mui/icons-material/Landscape';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import GrassIcon from '@mui/icons-material/Grass';
import ParkIcon from '@mui/icons-material/Park';
import WavesIcon from '@mui/icons-material/Waves';
import FilterHdrIcon from '@mui/icons-material/FilterHdr';
import PoolIcon from '@mui/icons-material/Pool';
import OpacityIcon from '@mui/icons-material/Opacity';
import AcUnitIcon from '@mui/icons-material/AcUnit';

const LandFilters = ({ 
  onFiltersChange, 
  availableRegions = [], 
  availableTerritories = [], 
  availableRarities = [],
  availableResources = [],
  availablePlotStatuses = [],
  availableLandTypes = [],
  availableWorksites = [],

}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Mobile responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Filter states
  const [sortOption, setSortOption] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [plotStatusFilter, setPlotStatusFilter] = useState('');
  const [landTypeFilter, setLandTypeFilter] = useState('');
  const [terrainFilter, setTerrainFilter] = useState('');
  const [worksiteFilter, setWorksiteFilter] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  // Define plot statuses with icons
  const plotStatuses = [
    { id: 'natural', name: 'Natural', icon: <NatureIcon />, color: colors.greenAccent[400] },
    { id: 'magical', name: 'Magical', icon: <AutoAwesomeIcon />, color: colors.blueAccent[400] },
    { id: 'occupied', name: 'Occupied', icon: <SecurityIcon />, color: colors.redAccent[400] }
  ];

  // Define land types with icons
  const landTypes = [
    { id: 'land', name: 'Land', icon: <TerrainIcon />, color: colors.greenAccent[400] },
    { id: 'castle', name: 'Castle', icon: <FortIcon />, color: colors.blueAccent[400] },
    { id: 'keep', name: 'Keep', icon: <AccountBalanceIcon />, color: colors.grey[400] }
  ];

  // Terrain types (matches the API's deed_type field exactly) - 14 fixed geography categories
  const terrainTypes = [
    { id: 'Badlands', name: 'Badlands', icon: <TerrainIcon />, color: '#A0522D' },
    { id: 'Bog', name: 'Bog', icon: <WaterIcon />, color: '#556B2F' },
    { id: 'Caldera', name: 'Caldera', icon: <LocalFireDepartmentIcon />, color: '#D2691E' },
    { id: 'Canyon', name: 'Canyon', icon: <LandscapeIcon />, color: '#CD853F' },
    { id: 'Desert', name: 'Desert', icon: <WbSunnyIcon />, color: '#EDC9AF' },
    { id: 'Forest', name: 'Forest', icon: <ForestIcon />, color: '#228B22' },
    { id: 'Hills', name: 'Hills', icon: <GrassIcon />, color: '#9ACD32' },
    { id: 'Jungle', name: 'Jungle', icon: <ParkIcon />, color: '#006400' },
    { id: 'Lake', name: 'Lake', icon: <WavesIcon />, color: '#4682B4' },
    { id: 'Mountain', name: 'Mountain', icon: <FilterHdrIcon />, color: '#708090' },
    { id: 'Plains', name: 'Plains', icon: <GrainIcon />, color: '#DAA520' },
    { id: 'River', name: 'River', icon: <PoolIcon />, color: '#1E90FF' },
    { id: 'Swamp', name: 'Swamp', icon: <OpacityIcon />, color: '#6B8E23' },
    { id: 'Tundra', name: 'Tundra', icon: <AcUnitIcon />, color: '#B0E0E6' }
  ];

  // Worksite types (matches the API's worksite_type field exactly)
  const worksiteTypes = [
    { id: 'Grain Farm', name: 'Grain Farm', icon: <AgricultureIcon />, color: '#DAA520' },
    { id: 'KEEP', name: 'Keep', icon: <CastleIcon />, color: colors.grey[400] },
    { id: 'Logging Camp', name: 'Logging Camp', icon: <ForestIcon />, color: '#8B4513' },
    { id: 'Ore Mine', name: 'Ore Mine', icon: <ConstructionIcon />, color: '#708090' },
    { id: 'Quarry', name: 'Quarry', icon: <TerrainIcon />, color: '#A9A9A9' },
    { id: 'Research Hut', name: 'Research Hut', icon: <ScienceIcon />, color: '#2196F3' },
    { id: 'Shard Mine', name: 'Shard Mine', icon: <DiamondIcon />, color: '#FF5722' }
  ];

  // Define rarities with icons and colors (using Splinterlands rarity icons)
  const rarities = [
    { 
      id: 'common', 
      name: 'Common', 
      color: '#8B7355',
      icon: 'https://d36mxiodymuqjm.cloudfront.net/website/create_team/icon_rarity_common_new.svg'
    },
    { 
      id: 'rare', 
      name: 'Rare', 
      color: '#5F9EA0',
      icon: 'https://d36mxiodymuqjm.cloudfront.net/website/create_team/icon_rarity_rare_new.svg'
    },
    { 
      id: 'epic', 
      name: 'Epic', 
      color: '#9370DB',
      icon: 'https://d36mxiodymuqjm.cloudfront.net/website/create_team/icon_rarity_epic_new.svg'
    },
    { 
      id: 'legendary', 
      name: 'Legendary', 
      color: '#FFD700',
      icon: 'https://d36mxiodymuqjm.cloudfront.net/website/create_team/icon_rarity_legendary_new.svg'
    },
  ];



  // Define resource types with icons - Updated to match API resource_id values (Fantasy-themed)
  const resourceTypes = [
    { id: 'grain', name: 'Grain', resourceId: 1, symbol: 'GRAIN', icon: <GrainIcon />, color: '#FFD700' },
    { id: 'sps', name: 'SPS', resourceId: 2, symbol: 'SPS', icon: <AutoAwesomeIcon />, color: '#9C27B0' },
    { id: 'research', name: 'Research', resourceId: 3, symbol: 'RESEARCH', icon: <PsychologyIcon />, color: '#2196F3' },
    { id: 'resource', name: 'Resource', resourceId: 4, symbol: 'RESOURCE', icon: <DiamondIcon />, color: '#FF5722' },
    { id: 'wood', name: 'Wood', resourceId: 5, symbol: 'WOOD', icon: <ForestIcon />, color: '#8B4513' },
    { id: 'stone', name: 'Stone', resourceId: 6, symbol: 'STONE', icon: <TerrainIcon />, color: '#696969' },
    { id: 'iron', name: 'Iron', resourceId: 7, symbol: 'IRON', icon: <HandymanIcon />, color: '#708090' },
    { id: 'ore', name: 'Ore', resourceId: 8, symbol: 'ORE', icon: <LocalFireDepartmentIcon />, color: '#8B4513' }
  ];

  // Notify parent component when filters change
  useEffect(() => {
    const filters = {
      sortOption,
      regionFilter,
      territoryFilter,
      rarityFilter,
      resourceFilter,
      plotStatusFilter,
      landTypeFilter,
      terrainFilter,
      worksiteFilter,
      priceRange
    };
    onFiltersChange(filters);
  }, [sortOption, regionFilter, territoryFilter, rarityFilter, resourceFilter, plotStatusFilter, landTypeFilter, terrainFilter, worksiteFilter, priceRange, onFiltersChange]);

  // Clear all filters
  const clearFilters = () => {
    setSortOption('');
    setRegionFilter('');
    setTerritoryFilter('');
    setRarityFilter('');
    setResourceFilter('');
    setPlotStatusFilter('');
    setLandTypeFilter('');
    setTerrainFilter('');
    setWorksiteFilter('');
    setPriceRange({ min: '', max: '' });
  };

  // Toggle plot status filter
  const togglePlotStatus = (statusId) => {
    setPlotStatusFilter(prev => prev === statusId ? '' : statusId);
  };

  // Toggle land type filter
  const toggleLandType = (typeId) => {
    setLandTypeFilter(prev => prev === typeId ? '' : typeId);
  };

  // Toggle rarity filter
  const toggleRarity = (rarityId) => {
    setRarityFilter(prev => prev === rarityId ? '' : rarityId);
  };

  // Toggle resource filter
  const toggleResource = (resourceId) => {
    setResourceFilter(prev => prev === resourceId ? '' : resourceId);
  };

  // Toggle terrain (geography) filter
  const toggleTerrain = (terrainId) => {
    setTerrainFilter(prev => prev === terrainId ? '' : terrainId);
  };

  // Toggle worksite filter
  const toggleWorksite = (worksiteId) => {
    setWorksiteFilter(prev => prev === worksiteId ? '' : worksiteId);
  };

  const activeFiltersCount = [regionFilter, territoryFilter, rarityFilter, resourceFilter, plotStatusFilter, landTypeFilter, terrainFilter, worksiteFilter, priceRange.min, priceRange.max].filter(Boolean).length;

  return (
    <Box sx={{ 
      px: isMobile ? 1 : 2, 
      py: isMobile ? 1 : 2,
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <Box display="flex" alignItems="center" gap={1} mb={isMobile ? 1 : 2}>
        <FilterListIcon sx={{ color: colors.greenAccent[400], fontSize: isMobile ? '1rem' : '1.2rem' }} />
        <Typography
          variant="h6"
          color={colors.grey[100]}
          sx={{ fontSize: isMobile ? '0.75rem' : '0.9rem', fontWeight: 'bold' }}
        >
          Land Filters
        </Typography>
        {activeFiltersCount > 0 && (
          <Chip 
            label={`${activeFiltersCount}`}
            size="small"
            sx={{ 
              backgroundColor: colors.redAccent[600],
              color: colors.grey[100],
              fontSize: isMobile ? '0.6rem' : '0.7rem',
              height: isMobile ? '16px' : '20px'
            }}
          />
        )}
      </Box>

      {activeFiltersCount > 0 && (
        <Button 
          size="small" 
          onClick={clearFilters}
          variant="outlined"
          sx={{ 
            color: colors.grey[300],
            borderColor: colors.primary[300],
            fontSize: isMobile ? '0.65rem' : '0.7rem',
            mb: isMobile ? 1 : 2,
            width: '100%',
            py: isMobile ? 0.25 : 0.5
          }}
        >
          Clear All Filters
        </Button>
      )}

      <Box display="flex" flexDirection="column" gap={isMobile ? 1 : 2}>
        {/* Sort Option */}
        <FormControl fullWidth size="small">
          <InputLabel sx={{ 
            color: colors.grey[300], 
            fontSize: isMobile ? '0.7rem' : '0.8rem' 
          }}>
            Sort By
          </InputLabel>
          <Select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            label="Sort By"
            sx={{
              color: colors.grey[100],
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              '.MuiOutlinedInput-notchedOutline': { borderColor: colors.primary[300] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[400] },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[400] },
              '& .MuiSelect-select': { 
                py: isMobile ? 0.75 : 1 
              }
            }}
          >
            <MenuItem value="" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>No Sorting</MenuItem>
            <MenuItem value="price_asc" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Price: Low to High</MenuItem>
            <MenuItem value="price_desc" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Price: High to Low</MenuItem>
            <MenuItem value="region_asc" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Region: Low to High</MenuItem>
            <MenuItem value="region_desc" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Region: High to Low</MenuItem>
            <MenuItem value="rarity_asc" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Rarity: A to Z</MenuItem>
            <MenuItem value="rarity_desc" sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>Rarity: Z to A</MenuItem>
          </Select>
        </FormControl>

        {/* Plot Status Filter - Button Style */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mb: 1 }}>
            Plot Status
          </Typography>
          <Box 
            display="flex" 
            flexWrap="wrap"
            gap={1}
            sx={{ 
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              p: 1
            }}
          >
            {plotStatuses.map(status => {
              const isSelected = plotStatusFilter === status.id;

              return (
                <Chip
                  key={status.id}
                  icon={status.icon}
                  label={status.name}
                  size="small"
                  onClick={() => togglePlotStatus(status.id)}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: isSelected ? status.color : 'transparent',
                    color: isSelected ? '#ffffff' : status.color,
                    border: `2px solid ${isSelected ? status.color : colors.grey[600]}`,
                    transition: 'all 0.2s',
                    '& .MuiChip-icon': { color: isSelected ? '#ffffff' : status.color },
                    '&:hover': {
                      backgroundColor: isSelected ? status.color : `${status.color}20`,
                      borderColor: status.color
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Land Type Filter - Button Style */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mb: 1 }}>
            Land Type
          </Typography>
          <Box 
            display="flex" 
            flexWrap="wrap"
            gap={1}
            sx={{ 
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              p: 1
            }}
          >
            {landTypes.map(type => {
              const isSelected = landTypeFilter === type.id;

              return (
                <Chip
                  key={type.id}
                  icon={type.icon}
                  label={type.name}
                  size="small"
                  onClick={() => toggleLandType(type.id)}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: isSelected ? type.color : 'transparent',
                    color: isSelected ? '#ffffff' : type.color,
                    border: `2px solid ${isSelected ? type.color : colors.grey[600]}`,
                    transition: 'all 0.2s',
                    '& .MuiChip-icon': { color: isSelected ? '#ffffff' : type.color },
                    '&:hover': {
                      backgroundColor: isSelected ? type.color : `${type.color}20`,
                      borderColor: type.color
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Geography (Terrain) Filter - Button Style */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mb: 1 }}>
            Geography
          </Typography>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1}
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              p: 1
            }}
          >
            {terrainTypes.map(terrain => {
              const isSelected = terrainFilter === terrain.id;

              return (
                <Chip
                  key={terrain.id}
                  icon={terrain.icon}
                  label={terrain.name}
                  size="small"
                  onClick={() => toggleTerrain(terrain.id)}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: isSelected ? terrain.color : 'transparent',
                    color: isSelected ? '#ffffff' : terrain.color,
                    border: `2px solid ${isSelected ? terrain.color : colors.grey[600]}`,
                    transition: 'all 0.2s',
                    '& .MuiChip-icon': { color: isSelected ? '#ffffff' : terrain.color },
                    '&:hover': {
                      backgroundColor: isSelected ? terrain.color : `${terrain.color}20`,
                      borderColor: terrain.color
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Worksite Filter - Button Style */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mb: 1 }}>
            Worksite
          </Typography>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1}
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              p: 1
            }}
          >
            {worksiteTypes.map(worksite => {
              const isSelected = worksiteFilter === worksite.id;

              return (
                <Chip
                  key={worksite.id}
                  icon={worksite.icon}
                  label={worksite.name}
                  size="small"
                  onClick={() => toggleWorksite(worksite.id)}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: isSelected ? worksite.color : 'transparent',
                    color: isSelected ? '#ffffff' : worksite.color,
                    border: `2px solid ${isSelected ? worksite.color : colors.grey[600]}`,
                    transition: 'all 0.2s',
                    '& .MuiChip-icon': { color: isSelected ? '#ffffff' : worksite.color },
                    '&:hover': {
                      backgroundColor: isSelected ? worksite.color : `${worksite.color}20`,
                      borderColor: worksite.color
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Rarity Filter - Button Style with Icons */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mb: 1 }}>
            Rarity
          </Typography>
          <Box 
            display="flex" 
            flexWrap="wrap"
            gap={1}
            sx={{ 
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              p: 1
            }}
          >
            {rarities.map(rarity => {
              const isSelected = rarityFilter === rarity.id;

              return (
                <Chip
                  key={rarity.id}
                  icon={
                    <img
                      src={rarity.icon}
                      alt={rarity.name}
                      style={{
                        width: '16px',
                        height: '16px',
                        filter: isSelected ? 'brightness(0) invert(1)' : 'none'
                      }}
                    />
                  }
                  label={rarity.name}
                  size="small"
                  onClick={() => toggleRarity(rarity.id)}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: isSelected ? rarity.color : 'transparent',
                    color: isSelected ? '#ffffff' : rarity.color,
                    border: `2px solid ${isSelected ? rarity.color : colors.grey[600]}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: isSelected ? rarity.color : `${rarity.color}20`,
                      borderColor: rarity.color
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Resource Filter - Button Style */}
        <Box>
          <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mb: 1 }}>
            Resource
          </Typography>
          <Box 
            display="flex" 
            flexWrap="wrap"
            gap={1}
            sx={{ 
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              p: 1
            }}
          >
            {resourceTypes.map(resource => {
              const isSelected = resourceFilter === resource.id;

              return (
                <Chip
                  key={resource.id}
                  icon={resource.icon}
                  label={resource.name}
                  size="small"
                  onClick={() => toggleResource(resource.id)}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: isSelected ? resource.color : 'transparent',
                    color: isSelected ? '#ffffff' : resource.color,
                    border: `2px solid ${isSelected ? resource.color : colors.grey[600]}`,
                    transition: 'all 0.2s',
                    '& .MuiChip-icon': { color: isSelected ? '#ffffff' : resource.color },
                    '&:hover': {
                      backgroundColor: isSelected ? resource.color : `${resource.color}20`,
                      borderColor: resource.color
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Region Filter */}
        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: colors.grey[300], fontSize: '0.8rem' }}>Region</InputLabel>
          <Select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            label="Region"
            sx={{
              color: colors.grey[100],
              fontSize: '0.8rem',
              '.MuiOutlinedInput-notchedOutline': { borderColor: colors.primary[300] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[400] },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[400] }
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All Regions</MenuItem>
            {availableRegions.map(region => (
              <MenuItem key={region} value={region} sx={{ fontSize: '0.8rem' }}>
                Region {region}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Territory Filter */}
        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: colors.grey[300], fontSize: '0.8rem' }}>Territory</InputLabel>
          <Select
            value={territoryFilter}
            onChange={(e) => setTerritoryFilter(e.target.value)}
            label="Territory"
            sx={{
              color: colors.grey[100],
              fontSize: '0.8rem',
              '.MuiOutlinedInput-notchedOutline': { borderColor: colors.primary[300] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[400] },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[400] }
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}>All Territories</MenuItem>
            {availableTerritories.map(territory => (
              <MenuItem key={territory} value={territory} sx={{ fontSize: '0.8rem' }}>
                {territory}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Price Range Filters */}
        <Typography variant="body2" color={colors.grey[300]} sx={{ fontSize: '0.8rem', mt: 1 }}>
          Price Range (USD)
        </Typography>
        
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              label="Min"
              type="number"
              size="small"
              value={priceRange.min}
              onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
              sx={{
                '& .MuiInputLabel-root': { 
                  color: colors.grey[300],
                  fontSize: '0.8rem'
                },
                '& .MuiOutlinedInput-root': {
                  color: colors.grey[100],
                  fontSize: '0.8rem',
                  '& fieldset': { borderColor: colors.primary[300] },
                  '&:hover fieldset': { borderColor: colors.greenAccent[400] },
                  '&.Mui-focused fieldset': { borderColor: colors.greenAccent[400] }
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Max"
              type="number"
              size="small"
              value={priceRange.max}
              onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
              sx={{
                '& .MuiInputLabel-root': { 
                  color: colors.grey[300],
                  fontSize: '0.8rem'
                },
                '& .MuiOutlinedInput-root': {
                  color: colors.grey[100],
                  fontSize: '0.8rem',
                  '& fieldset': { borderColor: colors.primary[300] },
                  '&:hover fieldset': { borderColor: colors.greenAccent[400] },
                  '&.Mui-focused fieldset': { borderColor: colors.greenAccent[400] }
                }
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LandFilters;
